import React, { useState } from 'react';
import { updateDisposalTool, uploadDisposalPhoto, loadToolMedia, deleteDisposalPhoto } from '../api/disposalApi';
import { useUser } from '../../../shared/context/UserContext';
import { useReferenceData } from '../../../shared/hooks/useReferenceData';
import { ComboBox } from '../../../shared/components/ComboBox';
import './DisposalDetailModal.css';

export default function DisposalDetailModal({ tool, onClose, onUpdate }) {
  const { callerEmail } = useUser();

  const toolSiteId = tool?.siteId || tool?.site_id;

  // The five location numbers render together when any one has a value, and
  // not at all when none does. Has-value test, so "0" renders as 0.
  const hasValue = v => v !== null && v !== undefined && v !== '';
  const nbr = v => (hasValue(v) ? String(v) : '-');
  const hasNbr = [tool.rowNbr, tool.stackNbr, tool.rackNbr, tool.shelfNbr, tool.skidNbr].some(hasValue);

  const { options: refOptions, addValue: addRefValue } = useReferenceData(
    ['marked', 'staged', 'scrap_status', 'scrap_project_number',
     'site_location', 'detail_location',
     'row_nbr', 'stack_nbr', 'rack_nbr', 'shelf_nbr', 'skid_nbr'],
    callerEmail,
    toolSiteId
  );

  const [isEditing, setIsEditing]         = useState(false);
  const [saving, setSaving]               = useState(false);
  const [editedTool, setEditedTool]       = useState({ ...tool });
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [deletingMediaId, setDeletingMediaId] = useState(null);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [mediaItems, setMediaItems]       = useState([]);  // uploaded photos from Cosmos
  const [mediaLoaded, setMediaLoaded]     = useState(false);

  // Handle ESC key to close modal
  React.useEffect(() => {
    function handleEscKey(event) {
      if (event.key === 'Escape') {
        if (isEditing) {
          if (window.confirm('Discard changes?')) {
            handleCancel();
          }
        } else {
          onClose();
        }
      }
    }
    document.addEventListener('keydown', handleEscKey);
    return () => document.removeEventListener('keydown', handleEscKey);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, onClose]);

  // Load media when modal opens
  React.useEffect(() => {
    if (!mediaLoaded && tool.internalToolId) {
      loadToolMedia(tool.internalToolId, callerEmail)
        .then(items => {
          setMediaItems(items || []);
          setMediaLoaded(true);
        })
        .catch(err => {
          console.warn('Could not load media:', err.message);
          setMediaLoaded(true);
        });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tool.internalToolId, mediaLoaded]);

  function handleInputChange(field, value) {
    setEditedTool(prev => ({ ...prev, [field]: value }));
  }

  // After a successful save, persist any new location values entered via combo box
  async function persistNewLocationValues() {
    const siteId = tool?.siteId || tool?.site_id;
    const locationFields = [
      { field: 'site_location',  value: editedTool.siteLoc },
      { field: 'detail_location', value: editedTool.sectionDetail },
      { field: 'row_nbr',        value: editedTool.rowNbr },
      { field: 'stack_nbr',      value: editedTool.stackNbr },
      { field: 'rack_nbr',       value: editedTool.rackNbr },
      { field: 'shelf_nbr',      value: editedTool.shelfNbr },
      { field: 'skid_nbr',       value: editedTool.skidNbr },
    ];
    for (const { field, value } of locationFields) {
      // The five can load as numbers, which have no .trim().
      const text = value == null ? '' : String(value).trim();
      if (text) {
        await addRefValue(field, text, siteId);
      }
    }
  }

  async function handleSave() {
    try {
      setSaving(true);
      const result = await updateDisposalTool(
        tool.internalDisposalId,
        tool.internalToolId,
        editedTool,
        callerEmail
      );

      if (result?.queued) {
        // Offline — update UI optimistically from local state
        onUpdate({ ...tool, ...editedTool });
      } else {
        onUpdate(result);
      }

      await persistNewLocationValues();

      // Persist new scrap_project_number to ReferenceData (global — project numbers span sites)
      if (editedTool.scrapProjectNumber) {
        const existing = refOptions.scrap_project_number || [];
        const trimmedScrap = editedTool.scrapProjectNumber.trim();
        if (trimmedScrap && !existing.includes(trimmedScrap)) {
          await addRefValue('scrap_project_number', trimmedScrap, null);
        }
      }

      setIsEditing(false);
      setShowSaveSuccess(true);
      setTimeout(() => setShowSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Error saving tool:', error);
      alert('Failed to save changes. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  function handleCancel() {
    setEditedTool({ ...tool });
    setIsEditing(false);
  }

  async function handleDeletePhoto(item) {
    // Pending (offline-queued) items: just remove from local state, no API call
    if (item.pending) {
      setMediaItems(prev => prev.filter(m => m.media_id !== item.media_id));
      return;
    }

    if (!window.confirm('Delete this photo? This cannot be undone.')) return;

    try {
      setDeletingMediaId(item.media_id);
      await deleteDisposalPhoto(item.media_id, callerEmail);
      setMediaItems(prev => prev.filter(m => m.media_id !== item.media_id));
    } catch (error) {
      console.error('Error deleting photo:', error);
      alert('Failed to delete photo. Please try again.');
    } finally {
      setDeletingMediaId(null);
    }
  }

  async function handleFileUpload(photoType, event) {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    // Guard against React StrictMode double-invoke
    if (uploadingMedia) return;

    const siteId = tool?.siteId || tool?.site_id || 'unknown-site';
    const uploadedBy = callerEmail || 'unknown-user';

    try {
      setUploadingMedia(true);

      for (const file of Array.from(files)) {
        const result = await uploadDisposalPhoto({
          toolId:     tool.internalToolId,
          disposalId: tool.internalDisposalId,
          photoType,
          file,
          uploadedBy,
          siteId,
          callerEmail,
        });

        if (result.queued) {
          // Offline — show pending indicator in media list
          setMediaItems(prev => [...prev, {
            media_id:    result.outbox_id,
            photo_type:  photoType,
            blob_url:    null,
            uploaded_at: result.queued_at,
            pending:     true,
          }]);
        } else {
          // Online — add real media item with blob URL
          setMediaItems(prev => [...prev, {
            media_id:    result.media_id,
            photo_type:  photoType,
            blob_url:    result.blob_url,
            uploaded_at: result.uploaded_at,
            pending:     false,
          }]);
        }
      }
    } catch (error) {
      console.error('Error uploading files:', error);
      alert('Failed to upload files. Please try again.');
    } finally {
      setUploadingMedia(false);
      // Clear the input so the same file can be re-selected if needed
      event.target.value = '';
    }
  }

  // Group media by type for display
  const bolMedia  = mediaItems.filter(m => m.photo_type === 'bol');
  const loadMedia = mediaItems.filter(m => m.photo_type === 'weigh_ticket');

  function MediaThumbnail({ item, isEditing, onDelete }) {
    const isDeleting = deletingMediaId === item.media_id;

    if (item.pending) {
      return (
        <div className="media-thumb-wrapper">
          <div className="media-thumb media-thumb--pending" title="Queued — will upload when synced">
            ⏳ Pending
          </div>
          {isEditing && (
            <button
              className="media-thumb-delete"
              onClick={() => onDelete(item)}
              title="Remove from queue"
              aria-label="Remove queued photo"
            >
              ✕
            </button>
          )}
        </div>
      );
    }

    return (
      <div className="media-thumb-wrapper">
        <a href={item.blob_url} target="_blank" rel="noopener noreferrer">
          <img
            src={item.blob_url}
            alt={item.photo_type}
            className={`media-thumb${isDeleting ? ' media-thumb--deleting' : ''}`}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        </a>
        {isEditing && (
          <button
            className="media-thumb-delete"
            onClick={() => onDelete(item)}
            disabled={isDeleting}
            title="Delete photo"
            aria-label="Delete photo"
          >
            {isDeleting ? '...' : '✕'}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="disposal-modal" onClick={(e) => e.stopPropagation()}>
        {/* Fixed position success message */}
        {showSaveSuccess && (
          <div className="save-success-notification">
            ✓ Changes saved successfully!
          </div>
        )}

        {/* Sticky Modal Header */}
        <div className="modal-header sticky-header">
          <div className="modal-header-content">
            <div className="modal-title-section">
              <h2>DISPOSAL RECORD DETAIL</h2>
              <p className="modal-subtitle">
                {isEditing ? 'Edit disposal information below' : 'Confirm update using asset pictures below.'}
              </p>
              <div className="tool-id-display">
                Tool ID: <strong>{tool.toolId}</strong>
              </div>
            </div>

            <button
              onClick={() => {
                if (isEditing) {
                  if (window.confirm('Discard changes?')) {
                    handleCancel();
                    onClose();
                  }
                } else {
                  onClose();
                }
              }}
              className="modal-close-x"
              aria-label="Close modal"
            >
              ✕
            </button>
          </div>

          <div className="modal-header-actions">
            {isEditing ? (
              <>
                <button onClick={handleSave} disabled={saving} className="btn-save">
                  {saving ? 'SAVING...' : 'SAVE CHANGES'}
                </button>
                <button onClick={handleCancel} disabled={saving} className="btn-cancel">
                  CANCEL
                </button>
              </>
            ) : (
              <button onClick={() => setIsEditing(true)} className="btn-edit">
                EDIT RECORD
              </button>
            )}
          </div>
        </div>

        <div className="modal-content">
          {/* Tool Identification */}
          <div className="detail-section">
            <div className="section-row">
              <div className="section-label">TOOL ID</div>
              <div className="section-value">{tool.toolId}</div>
            </div>

            {/* Tool photos (from Media container) */}
            <div className="tool-photos">
              {!mediaLoaded ? (
                <div className="no-photos">Loading photos...</div>
              ) : mediaItems.length > 0 ? (
                mediaItems.map((item) => (
                  <MediaThumbnail key={item.media_id} item={item} isEditing={isEditing} onDelete={handleDeletePhoto} />
                ))
              ) : (
                <div className="no-photos">No photos available</div>
              )}
            </div>

            <div className="section-row">
              <div className="section-label">EST. WEIGHT:</div>
              <div className="section-value">
                {tool.weight ? tool.weight.toLocaleString() : 'N/A'} {tool.weightUnit}
              </div>
            </div>

            <div className="section-row">
              <div className="section-label">EST. SIZE:</div>
              <div className="section-value">{tool.size || 'N/A'}</div>
            </div>

            <div className="section-row">
              <div className="section-label">SCRAP PROJECT #:</div>
              <div className="section-value">
                {isEditing ? (
                  <ComboBox
                    value={editedTool.scrapProjectNumber || ''}
                    onChange={(v) => handleInputChange('scrapProjectNumber', v)}
                    options={refOptions.scrap_project_number || ['TBD', 'SUBMITTED']}
                    placeholder="TBD, SUBMITTED, or project number"
                    className="edit-input"
                  />
                ) : (
                  tool.scrapProjectNumber || 'TBD'
                )}
              </div>
            </div>

            <div className="section-row">
              <div className="section-label">OEM:</div>
              <div className="section-value">{tool.oem}</div>
            </div>
          </div>

          {/* Location Details */}
          <div className="detail-grid">
            <div className="grid-item">
              <div className="grid-label">SITE LOC</div>
              <div className="grid-value">{tool.siteLoc}</div>
            </div>
            <div className="grid-item">
              <div className="grid-label">SECTION / DETAIL LOCATION</div>
              <div className="grid-value">{tool.sectionDetail}</div>
            </div>
            {hasNbr && (
              <>
                <div className="grid-item">
                  <div className="grid-label">Row Nbr</div>
                  <div className="grid-value">{nbr(tool.rowNbr)}</div>
                </div>
                <div className="grid-item">
                  <div className="grid-label">Stack Nbr</div>
                  <div className="grid-value">{nbr(tool.stackNbr)}</div>
                </div>
                <div className="grid-item">
                  <div className="grid-label">Rack Nbr</div>
                  <div className="grid-value">{nbr(tool.rackNbr)}</div>
                </div>
                <div className="grid-item">
                  <div className="grid-label">Shelf Nbr</div>
                  <div className="grid-value">{nbr(tool.shelfNbr)}</div>
                </div>
                <div className="grid-item">
                  <div className="grid-label">Skid Nbr</div>
                  <div className="grid-value">{nbr(tool.skidNbr)}</div>
                </div>
              </>
            )}
          </div>

          {/* Disposal Workflow Fields */}
          <div className="detail-grid">
            <div className="grid-item">
              <div className="grid-label">TOOL MARKED</div>
              <div className="grid-value">
                {isEditing ? (
                  <select
                    value={editedTool.marked || ''}
                    onChange={(e) => handleInputChange('marked', e.target.value)}
                    className="edit-select"
                  >
                    <option value="">Select...</option>
                    {(refOptions.marked || ['LOCATE', 'MARKED']).map(v => (
                      <option key={v} value={v}>{v}</option>
                    ))}
                  </select>
                ) : (
                  <span className={`status-badge ${(tool.marked || '').toLowerCase()}`}>
                    {tool.marked || '-'}
                  </span>
                )}
              </div>
            </div>

            <div className="grid-item">
              <div className="grid-label">STAGED</div>
              <div className="grid-value">
                {isEditing ? (
                  <select
                    value={editedTool.staged || ''}
                    onChange={(e) => handleInputChange('staged', e.target.value)}
                    className="edit-select"
                  >
                    <option value="">Select...</option>
                    {(refOptions.staged || ['STAGED', 'SHIPPED']).map(v => (
                      <option key={v} value={v}>{v}</option>
                    ))}
                  </select>
                ) : (
                  tool.staged || '-'
                )}
              </div>
            </div>

            <div className="grid-item">
              <div className="grid-label">SCRAP STATUS</div>
              <div className="grid-value">
                {isEditing ? (
                  <select
                    value={editedTool.scrapStatus || ''}
                    onChange={(e) => handleInputChange('scrapStatus', e.target.value)}
                    className="edit-select"
                  >
                    <option value="">Select...</option>
                    {(refOptions.scrap_status || ['SCRAPPED', 'NOT SCRAPPED']).map(v => (
                      <option key={v} value={v}>{v}</option>
                    ))}
                  </select>
                ) : (
                  tool.scrapStatus || '-'
                )}
              </div>
            </div>

            <div className="grid-item">
              <div className="grid-label">SHIP DATE</div>
              <div className="grid-value">
                {isEditing ? (
                  <input
                    type="date"
                    value={editedTool.shipDate || ''}
                    onChange={(e) => handleInputChange('shipDate', e.target.value)}
                    className="edit-input"
                  />
                ) : (
                  tool.shipDate || '-'
                )}
              </div>
            </div>
          </div>

          {/* Logistics Fields */}
          <div className="detail-grid">
            <div className="grid-item">
              <div className="grid-label">TRUCK NBR</div>
              <div className="grid-value">
                {isEditing ? (
                  <input
                    type="text"
                    value={editedTool.truckNbr || ''}
                    onChange={(e) => handleInputChange('truckNbr', e.target.value)}
                    className="edit-input"
                  />
                ) : (
                  tool.truckNbr || '-'
                )}
              </div>
            </div>

            <div className="grid-item">
              <div className="grid-label">DAY TRIP NBR</div>
              <div className="grid-value">
                {isEditing ? (
                  <input
                    type="text"
                    value={editedTool.dayTripNbr || ''}
                    onChange={(e) => handleInputChange('dayTripNbr', e.target.value)}
                    className="edit-input"
                  />
                ) : (
                  tool.dayTripNbr || '-'
                )}
              </div>
            </div>

            <div className="grid-item">
              <div className="grid-label">WEIGH TICKET PHOTOS</div>
              <div className="grid-value">
                {isEditing && (
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={(e) => handleFileUpload('weigh_ticket', e)}
                    disabled={uploadingMedia}
                    className="file-input"
                  />
                )}
                {loadMedia.length > 0 ? (
                  <div className="media-thumbs-row">
                    {loadMedia.map(item => <MediaThumbnail key={item.media_id} item={item} isEditing={isEditing} onDelete={handleDeletePhoto} />)}
                  </div>
                ) : (
                  !isEditing && <span>-</span>
                )}
              </div>
            </div>

            <div className="grid-item">
              <div className="grid-label">BOL NBR</div>
              <div className="grid-value">
                {isEditing ? (
                  <input
                    type="text"
                    value={editedTool.bolNbr || ''}
                    onChange={(e) => handleInputChange('bolNbr', e.target.value)}
                    className="edit-input"
                  />
                ) : (
                  tool.bolNbr || '-'
                )}
              </div>
            </div>
          </div>

          {/* BOL Media and Comments */}
          <div className="detail-grid">
            <div className="grid-item span-2">
              <div className="grid-label">BOL PHOTOS</div>
              <div className="grid-value">
                {isEditing && (
                  <input
                    type="file"
                    multiple
                    accept="image/*,application/pdf"
                    onChange={(e) => handleFileUpload('bol', e)}
                    disabled={uploadingMedia}
                    className="file-input"
                  />
                )}
                {bolMedia.length > 0 ? (
                  <div className="media-thumbs-row">
                    {bolMedia.map(item => <MediaThumbnail key={item.media_id} item={item} isEditing={isEditing} onDelete={handleDeletePhoto} />)}
                  </div>
                ) : (
                  !isEditing && <span>-</span>
                )}
                {uploadingMedia && <div className="media-uploading">⏳ Processing...</div>}
              </div>
            </div>

            <div className="grid-item span-2">
              <div className="grid-label">DISPOSAL PROJECT COMMENTS</div>
              <div className="grid-value">
                {isEditing ? (
                  <textarea
                    value={editedTool.disposalComments || ''}
                    onChange={(e) => handleInputChange('disposalComments', e.target.value)}
                    className="edit-textarea"
                    rows="3"
                  />
                ) : (
                  tool.disposalComments || '-'
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
